import { useCallback, useRef, useState } from "react";
import { getStreamAuthToken } from "../../../../../auth/streamAuth";
import { API_BASE_URL } from "../../../../../api/axios";
import type { BulkRunEvent } from "../utils/constants";

export interface BulkRunState {
  status: "idle" | "running" | "complete" | "aborted" | "failed";
  events: BulkRunEvent[];
  totalOperations: number;
  completedOperations: number;
  failedOperation?: { migrationId: number; target: string; error?: string };
}

export function useBulkRun() {
  const [state, setState] = useState<BulkRunState>({
    status: "idle",
    events: [],
    totalOperations: 0,
    completedOperations: 0,
  });

  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(
    (migrationIds: number[], targets: string[], options: { backup: boolean }) => {
      setState({ status: "running", events: [], totalOperations: 0, completedOperations: 0 });

      // EventSource doesn't support POST bodies — use fetch + ReadableStream instead
      const controller = new AbortController();
      abortRef.current = controller;

      void (async () => {
        try {
          // Same base URL + Bearer auth as the axios layer — a relative "/api"
          // path would hit the portal's own nginx in production, not the API.
          // Token comes from the shared resolver so a Better Auth operator is
          // not authenticated with a stale OIDC identity (or none at all).
          const token = await getStreamAuthToken();

          const res = await fetch(`${API_BASE_URL}/super-admin/database/migrations/bulk-run`, {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Accept: "text/event-stream",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ migrationIds, targets, options }),
            signal: controller.signal,
          });

          if (!res.ok || !res.body) {
            throw new Error(`bulk-run HTTP ${res.status}`);
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            // SSE events are separated by double newlines
            let idx: number;
            while ((idx = buffer.indexOf("\n\n")) >= 0) {
              const chunk = buffer.slice(0, idx);
              buffer = buffer.slice(idx + 2);
              const dataLine = chunk.split("\n").find((line) => line.startsWith("data:"));
              if (!dataLine) continue;
              try {
                const event: BulkRunEvent = JSON.parse(dataLine.slice(5).trim());
                setState((prev) => {
                  const next: BulkRunState = { ...prev, events: [...prev.events, event] };
                  if (event.type === "start") {
                    next.totalOperations = event.totalOperations ?? 0;
                  }
                  if (event.type === "success" || event.type === "skipped") {
                    next.completedOperations = prev.completedOperations + 1;
                  }
                  if (event.type === "failure") {
                    next.failedOperation = {
                      migrationId: event.migrationId!,
                      target: event.target!,
                      error: event.error,
                    };
                    next.status = "failed";
                  }
                  if (event.type === "aborted") next.status = "aborted";
                  if (event.type === "complete") next.status = "complete";
                  return next;
                });
              } catch {
                // skip malformed SSE line
              }
            }
          }
        } catch {
          setState((prev) => ({ ...prev, status: "failed" }));
        } finally {
          abortRef.current = null;
        }
      })();
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const reset = useCallback(() => {
    setState({ status: "idle", events: [], totalOperations: 0, completedOperations: 0 });
  }, []);

  return { state, start, cancel, reset };
}
